// Email helper — calls the Supabase send-email Edge Function
import { supabase } from "@/lib/supabase";
import { reportBackgroundFailure } from "@/lib/errorReport";

// Platform-wide fallback admin inbox, used only when a property hasn't set
// its own notification address. Multi-tenant: each property's admin email
// lives in public.properties.admin_email and is preferred (see
// resolveAdminEmails). One place to change the default.
export const DEFAULT_ADMIN_EMAIL = "info@nestd.co.za";

/**
 * All admin/notification recipients for a property, resolved server-side by
 * property_notify_emails() (migration 034): the property's admin_email +
 * every admin/owner profile at the property + the company owner — so alerts
 * reach admin AND owner. SECURITY DEFINER there means it resolves correctly
 * even when the sender (e.g. a student) can't read companies/owner rows under
 * RLS. Never throws — any failure (incl. the function not being deployed yet)
 * returns the platform default.
 */
export async function resolveAdminEmails(
  propertyId?: string | null,
): Promise<string[]> {
  if (!propertyId) return [DEFAULT_ADMIN_EMAIL];
  try {
    const { data, error } = await supabase.rpc("property_notify_emails", {
      p_property_id: propertyId,
    });
    if (error) {
      // Forward so we notice when the RPC starts misbehaving, but still fall
      // back to the platform default so the user-facing flow continues.
      reportBackgroundFailure("email.resolveAdminEmails", error, { propertyId });
      return [DEFAULT_ADMIN_EMAIL];
    }
    if (!Array.isArray(data) || data.length === 0) {
      return [DEFAULT_ADMIN_EMAIL];
    }
    const emails = (data as unknown[])
      .map((e) => String(e).trim())
      .filter(Boolean);
    return emails.length ? emails : [DEFAULT_ADMIN_EMAIL];
  } catch (e) {
    reportBackgroundFailure("email.resolveAdminEmails", e, { propertyId });
    return [DEFAULT_ADMIN_EMAIL];
  }
}

/** Send to a property's admin + owner recipients (default fallback). */
export async function sendAdminEmail({
  propertyId,
  subject,
  html,
}: {
  propertyId?: string | null;
  subject: string;
  html: string;
}) {
  const to = await resolveAdminEmails(propertyId);
  return sendEmail({ to, subject, html });
}

export async function sendEmailToAll({
  studentEmail,
  subject,
  studentHtml,
  adminHtml,
  propertyId,
}: {
  studentEmail: string;
  subject: string;
  studentHtml: string;
  adminHtml: string;
  /** When provided, the admin copy goes to the property's admin + owner. */
  propertyId?: string | null;
}) {
  const adminTo = await resolveAdminEmails(propertyId);
  await Promise.all([
    sendEmail({ to: studentEmail, subject, html: studentHtml }),
    sendEmail({ to: adminTo, subject: `[M House] ${subject}`, html: adminHtml }),
  ]);
}

export async function sendEmail({
  to,
  subject,
  html,
}: {
  /** A single address or several (e.g. admin + owner). */
  to: string | string[];
  subject: string;
  html: string;
}) {
  const { data, error } = await supabase.functions.invoke("send-email", {
    body: { to, subject, html },
  });
  if (error) {
    // Best-effort transactional email — log + Sentry-forward so we notice
    // outages, but don't throw (callers expect a non-rejecting promise).
    reportBackgroundFailure("email.send", error, {
      subject,
      recipientCount: Array.isArray(to) ? to.length : 1,
    });
  }
  return { data, error };
}

const baseStyle = `font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:#0a0a0a;color:#f4efe6;border-radius:12px;`;
const gold = `#C9A84C`;
const muted = `#888`;
const btn = `display:inline-block;margin-top:24px;padding:12px 24px;background:${gold};color:#000;border-radius:8px;text-decoration:none;font-weight:600;`;
const footer = `<p style="color:#666;font-size:12px;margin-top:32px;">— The M House Team · Powered by Nestd</p>`;

export function rentReminderEmailHtml(
  name: string,
  balance: number,
  dueDate: string | null,
) {
  const amount = `R ${Number(balance).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
  const due = dueDate
    ? new Date(dueDate).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })
    : null;
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:22px;margin-bottom:8px;">Rent payment reminder</h1>
    <p>Hi ${name},</p>
    <p style="color:${muted};font-size:14px;">This is a friendly reminder that your rent balance of <strong style="color:#f4efe6;">${amount}</strong> ${due ? `was due on ${due}` : "is outstanding"}. Please settle it as soon as possible to avoid a late penalty.</p>
    <p style="color:${muted};font-size:14px;">If you have already paid, please ignore this message — and you can upload your proof of payment in your portal.</p>
    <a href="https://app.nestd.co.za/login" style="${btn}">Open your portal</a>
    ${footer}
  </div>`;
}

export function approvalEmailHtml(name: string, propertyName: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">Welcome to ${propertyName}! 🎉</h1>
    <p>Hi ${name},</p>
    <p style="color:${muted};font-size:14px;">Your application has been approved. You can now log in to your resident portal to view your lease, book transport, and more.</p>
    <a href="https://app.nestd.co.za/login" style="${btn}">Access your portal</a>
    ${footer}
  </div>`;
}

export function rejectionEmailHtml(name: string, reason: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:#e74c3c;font-size:24px;margin-bottom:8px;">Application Update</h1>
    <p>Hi ${name},</p>
    <p style="color:${muted};font-size:14px;">Unfortunately your application was not approved at this time.</p>
    <div style="background:#1a0a0a;border:1px solid #552323;border-radius:8px;padding:16px;margin:16px 0;">
      <p style="color:#e74c3c;font-size:11px;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:4px;">Reason</p>
      <p style="color:#f4efe6;font-size:14px;">${reason}</p>
    </div>
    <p style="color:${muted};font-size:14px;">To appeal, contact us at <a href="mailto:info@svillage.co.za" style="color:${gold};">info@svillage.co.za</a> or call +27 82 847 6327.</p>
    ${footer}
  </div>`;
}

export function contractSentEmailHtml(name: string, propertyName: string, leaseStart: string, leaseEnd: string, monthlyRate: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">Your lease is ready to sign ✍️</h1>
    <p>Hi ${name},</p>
    <p style="color:${muted};font-size:14px;">Your lease agreement for <strong style="color:#f4efe6;">${propertyName}</strong> has been generated and is ready for your digital signature.</p>
    <div style="background:#111;border:1px solid #222;border-radius:8px;padding:16px;margin:16px 0;">
      <p style="color:${muted};font-size:12px;margin-bottom:4px;">LEASE PERIOD</p>
      <p style="color:#f4efe6;font-size:14px;">${leaseStart} → ${leaseEnd}</p>
      <p style="color:${muted};font-size:12px;margin-top:12px;margin-bottom:4px;">MONTHLY RATE</p>
      <p style="color:${gold};font-size:20px;font-weight:600;">${monthlyRate}</p>
    </div>
    <p style="color:${muted};font-size:14px;">Please sign as soon as possible to confirm your accommodation.</p>
    <a href="https://app.nestd.co.za/student/contract" style="${btn}">Sign your lease</a>
    ${footer}
  </div>`;
}

export function contractSignedAdminEmailHtml(studentName: string, propertyName: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">Lease signed by student ✅</h1>
    <p>Hi,</p>
    <p style="color:${muted};font-size:14px;"><strong style="color:#f4efe6;">${studentName}</strong> has digitally signed their lease agreement for <strong style="color:#f4efe6;">${propertyName}</strong>.</p>
    <p style="color:${muted};font-size:14px;">Please review and countersign to fully execute the lease.</p>
    <a href="https://app.nestd.co.za/admin/contracts" style="${btn}">View contracts</a>
    ${footer}
  </div>`;
}

export function cancellationApprovedEmailHtml(name: string, propertyName: string, exitDate: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">Your move-out is approved ✅</h1>
    <p>Hi ${name},</p>
    <p style="color:${muted};font-size:14px;">Admin has approved your move-out notice for <strong style="color:#f4efe6;">${propertyName}</strong>. Please sign the cancellation letter to confirm.</p>
    <div style="background:#111;border:1px solid #222;border-radius:8px;padding:16px;margin:16px 0;">
      <p style="color:${muted};font-size:12px;margin-bottom:4px;">EXIT DATE</p>
      <p style="color:${gold};font-size:20px;font-weight:600;">${exitDate}</p>
    </div>
    <a href="https://app.nestd.co.za/student/move-out" style="${btn}">Sign cancellation letter</a>
    ${footer}
  </div>`;
}

export function cancellationSignedAdminEmailHtml(studentName: string, propertyName: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">Resident signed cancellation ✍️</h1>
    <p>Hi,</p>
    <p style="color:${muted};font-size:14px;"><strong style="color:#f4efe6;">${studentName}</strong> has signed their lease-cancellation letter for <strong style="color:#f4efe6;">${propertyName}</strong>.</p>
    <p style="color:${muted};font-size:14px;">Please review and countersign to finalise it.</p>
    <a href="https://app.nestd.co.za/admin/students" style="${btn}">View students</a>
    ${footer}
  </div>`;
}

export function cancellationFinalisedEmailHtml(name: string, propertyName: string, exitDate: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">Cancellation letter finalised 📄</h1>
    <p>Hi ${name},</p>
    <p style="color:${muted};font-size:14px;">Both parties have signed your lease-cancellation letter for <strong style="color:#f4efe6;">${propertyName}</strong>. Your tenancy ends on the exit date below.</p>
    <div style="background:#111;border:1px solid #222;border-radius:8px;padding:16px;margin:16px 0;">
      <p style="color:${muted};font-size:12px;margin-bottom:4px;">EXIT DATE</p>
      <p style="color:${gold};font-size:20px;font-weight:600;">${exitDate}</p>
    </div>
    <p style="color:${muted};font-size:14px;">A PDF copy becomes available to download from your move-out page 30 days after your notice date.</p>
    <a href="https://app.nestd.co.za/student/move-out" style="${btn}">View move-out</a>
    ${footer}
  </div>`;
}

export function maintenanceNewAdminEmailHtml(ticketTitle: string, category: string, priority: string, loggedBy: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">New maintenance ticket 🔧</h1>
    <p style="color:${muted};font-size:14px;">A new maintenance ticket has been logged by <strong style="color:#f4efe6;">${loggedBy}</strong>.</p>
    <div style="background:#111;border:1px solid #222;border-radius:8px;padding:16px;margin:16px 0;">
      <p style="color:${muted};font-size:12px;margin-bottom:4px;">TICKET</p>
      <p style="color:#f4efe6;font-size:14px;">${ticketTitle}</p>
      <p style="color:${muted};font-size:12px;margin-top:12px;margin-bottom:4px;">CATEGORY · PRIORITY</p>
      <p style="color:${gold};font-size:14px;font-weight:600;">${category} · ${priority}</p>
    </div>
    <a href="https://app.nestd.co.za/admin/maintenance" style="${btn}">View maintenance</a>
    ${footer}
  </div>`;
}

export function maintenanceUpdateEmailHtml(name: string, ticketTitle: string, newStatus: string) {
  const statusLabel: Record<string, string> = {
    in_progress: "In Progress",
    resolved: "Resolved",
    closed: "Closed",
    cancelled: "Cancelled",
  };
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">Maintenance update 🔧</h1>
    <p>Hi ${name},</p>
    <p style="color:${muted};font-size:14px;">Your maintenance ticket has been updated.</p>
    <div style="background:#111;border:1px solid #222;border-radius:8px;padding:16px;margin:16px 0;">
      <p style="color:${muted};font-size:12px;margin-bottom:4px;">TICKET</p>
      <p style="color:#f4efe6;font-size:14px;">${ticketTitle}</p>
      <p style="color:${muted};font-size:12px;margin-top:12px;margin-bottom:4px;">STATUS</p>
      <p style="color:${gold};font-size:14px;font-weight:600;">${statusLabel[newStatus] ?? newStatus}</p>
    </div>
    <a href="https://app.nestd.co.za/student/maintenance" style="${btn}">View ticket</a>
    ${footer}
  </div>`;
}

export function transportConfirmedEmailHtml(name: string, pickupTime: string, destination: string) {
  return `<div style="${baseStyle}">
    <h1 style="color:${gold};font-size:24px;margin-bottom:8px;">Your ride is confirmed 🚌</h1>
    <p>Hi ${name},</p>
    <p style="color:${muted};font-size:14px;">Your transport has been confirmed by the driver.</p>
    <div style="background:#111;border:1px solid #222;border-radius:8px;padding:16px;margin:16px 0;">
      <p style="color:${muted};font-size:12px;margin-bottom:4px;">PICKUP TIME</p>
      <p style="color:${gold};font-size:20px;font-weight:600;">${pickupTime}</p>
      <p style="color:${muted};font-size:12px;margin-top:12px;margin-bottom:4px;">DESTINATION</p>
      <p style="color:#f4efe6;font-size:14px;">${destination}</p>
    </div>
    <p style="color:${muted};font-size:14px;">Please be ready at your pickup location on time.</p>
    ${footer}
  </div>`;
}

export function contractSentAdminEmailHtml(studentName: string, propertyName: string) {
  return `<div style="font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:#0a0a0a;color:#f4efe6;border-radius:12px;">
    <h1 style="color:#C9A84C;font-size:24px;margin-bottom:8px;">Contract sent to student 📄</h1>
    <p>A lease agreement has been sent to <strong>${studentName}</strong> at <strong>${propertyName}</strong> for digital signature.</p>
    <a href="https://app.nestd.co.za/admin/contracts" style="display:inline-block;margin-top:24px;padding:12px 24px;background:#C9A84C;color:#000;border-radius:8px;text-decoration:none;font-weight:600;">View contracts</a>
    <p style="color:#666;font-size:12px;margin-top:32px;">— Nestd</p>
  </div>`;
}

export function maintenanceUpdateAdminEmailHtml(studentName: string, ticketTitle: string, newStatus: string) {
  return `<div style="font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:#0a0a0a;color:#f4efe6;border-radius:12px;">
    <h1 style="color:#C9A84C;font-size:24px;margin-bottom:8px;">Maintenance ticket updated 🔧</h1>
    <p>Ticket <strong>${ticketTitle}</strong> for <strong>${studentName}</strong> has been updated to <strong>${newStatus}</strong>.</p>
    <a href="https://app.nestd.co.za/admin/maintenance" style="display:inline-block;margin-top:24px;padding:12px 24px;background:#C9A84C;color:#000;border-radius:8px;text-decoration:none;font-weight:600;">View maintenance</a>
    <p style="color:#666;font-size:12px;margin-top:32px;">— Nestd</p>
  </div>`;
}

export function transportConfirmedAdminEmailHtml(studentName: string, pickupTime: string, destination: string) {
  return `<div style="font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:#0a0a0a;color:#f4efe6;border-radius:12px;">
    <h1 style="color:#C9A84C;font-size:24px;margin-bottom:8px;">Transport confirmed 🚌</h1>
    <p>Driver has confirmed pickup for <strong>${studentName}</strong> at <strong>${pickupTime}</strong> to <strong>${destination}</strong>.</p>
    <p style="color:#666;font-size:12px;margin-top:32px;">— Nestd</p>
  </div>`;
}
